// Money display mode — Henry quotes ex GST, so ex is the default; the
// Settings → Appearance seg flips every invoice/expense-derived figure at
// once. Storage stays inc-GST (amount_cents incl. GST + gst_cents); this is
// purely a display-layer choice. Since phase 25 the mode syncs across
// devices: D1 (settings table) is authoritative, localStorage is only a
// first-paint cache reconciled by syncMoneyMode() once authed.

import { useSyncExternalStore } from 'react';
import { api } from './api';
import { normalizeMoneyMode, type MoneyMode } from '../../shared/prefs';

export type { MoneyMode };

const KEY = 'ss-os-money';

let mode: MoneyMode = normalizeMoneyMode(localStorage.getItem(KEY)) ?? 'ex';
const listeners = new Set<() => void>();

function apply(next: MoneyMode): void {
  mode = next;
  localStorage.setItem(KEY, next);
  listeners.forEach((fn) => fn());
}

export function setMoneyMode(next: MoneyMode): void {
  apply(next);
  // Push to D1 so the other device picks it up; a failed push keeps the
  // local choice (it re-syncs from whichever device wrote last).
  api('/api/admin/system/prefs', { method: 'PUT', json: { moneyMode: next } }).catch(() => {});
}

/** Reconcile the first-paint cache against D1. Call once per session, after auth. */
export async function syncMoneyMode(): Promise<void> {
  const before = mode;
  try {
    const { moneyMode } = await api<{ moneyMode: MoneyMode }>('/api/admin/system/prefs');
    // If Henry flipped the seg while the fetch was in flight, his newer
    // choice wins — it's already on its way to D1.
    if (mode !== before) return;
    if (moneyMode !== mode) apply(moneyMode);
  } catch {
    // Offline / expired session: the cache stays until the next sync.
  }
}

export function useMoneyMode(): MoneyMode {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => mode,
  );
}

/** Display cents for a stored inc-GST amount + its GST component. */
export function displayCents(amountCents: number, gstCents: number, m: MoneyMode): number {
  return m === 'ex' ? amountCents - gstCents : amountCents;
}
